import Swiper from "swiper/bundle";

window.swipers = {};

/**
 * Toggle scroll-start / scroll-end CSS classes on a swiper container element.
 *
 * @param {Swiper} $this - The Swiper instance to inspect.
 * @param {HTMLElement | null} $swiperContainer - The container element to toggle classes on.
 * @returns {void}
 */
export function scrollClasses($this, $swiperContainer) {
    if ($swiperContainer !== null) {
        if ($this.isBeginning) {
            $swiperContainer.classList.add("scroll-start");
        } else {
            $swiperContainer.classList.remove("scroll-start");
        }

        if ($this.isEnd) {
            $swiperContainer.classList.add("scroll-end");
        } else {
            $swiperContainer.classList.remove("scroll-end");
        }
    }
}

/**
 * A horizontal/vertical free-scroll Swiper wrapper with scroll-start/end class management.
 */
export class swiperScroll {
    /**
     * Create a swiperScroll instance.
     *
     * @param {string} id - The DOM id of the swiper container element.
     * @param {Record<string, any>} options - Swiper configuration options.
     */
    constructor(id, options) {
        this._swiperId = id;
        this._initialOptions = options;

        /** @type {Swiper | undefined} */
        this._swiper = undefined;

        this.initialize(this._initialOptions);
        this.scroll(this._swiperId);
    }

    /**
     * Create the underlying Swiper instance and apply initial scroll classes.
     *
     * @param {Record<string, any>} options - Swiper configuration options.
     * @returns {void}
     */
    initialize(options) {
        this._swiper = new Swiper(`#${this._swiperId}`, options);
        const $swiperContainer = document.getElementById(this._swiperId);

        scrollClasses(this._swiper, $swiperContainer);
    }

    /**
     * Bind scroll-class updates to swiper events.
     *
     * @param {string} id - The DOM id of the swiper container element.
     * @returns {void}
     */
    scroll(id) {
        const $swiperContainer = document.getElementById(id);
        const swiper = /** @type {Swiper} */ (this._swiper);

        /**
         * Update scroll-start/end classes when the swiper position changes.
         *
         * @param {Swiper} self - The Swiper instance that fired the event.
         */
        const updateScroll = function (self) {
            scrollClasses(self, $swiperContainer);
        };
        swiper.on("reachBeginning", updateScroll);
        swiper.on("sliderMove", updateScroll);
        swiper.on("scroll", updateScroll);
    }

    /**
     * Update the underlying Swiper layout.
     *
     * @returns {void}
     */
    update() {
        /** @type {Swiper} */ (this._swiper).update();
    }

    /**
     * Update slide classes on the underlying Swiper instance.
     *
     * @returns {void}
     */
    updateSlidesClasses() {
        /** @type {Swiper} */ (this._swiper).updateSlidesClasses();
    }

    /**
     * Set the translate value on the underlying Swiper instance.
     *
     * @param {number} translate - The translate value in pixels.
     * @returns {void}
     */
    setTranslate(translate) {
        /** @type {Swiper} */ (this._swiper).setTranslate(translate);
    }

    /**
     * Update the layout and then set the translate value.
     *
     * @param {number} translate - The translate value in pixels.
     * @returns {void}
     */
    updateTranslate(translate) {
        this.update();
        this.setTranslate(translate);
    }

    /**
     * Get the current translate value from the underlying Swiper instance.
     *
     * @returns {number} The current translate value.
     */
    getCurrentTranslate() {
        return /** @type {Swiper} */ (this._swiper).translate;
    }
}

/**
 * A standard slide-based Swiper wrapper with scroll-start/end class management.
 */
export class swiperSlider {
    /**
     * Create a swiperSlider instance.
     *
     * @param {string} id - The DOM id of the swiper container element.
     * @param {Record<string, any>} options - Swiper configuration options.
     */
    constructor(id, options) {
        this._swiperId = id;
        this._initialOptions = options;

        /** @type {Swiper | undefined} */
        this._swiper = undefined;

        this.initialize(options);
    }

    /**
     * Create the underlying Swiper instance and apply initial scroll classes.
     *
     * @param {Record<string, any>} options - Swiper configuration options.
     * @returns {void}
     */
    initialize(options) {
        this._swiper = new Swiper(`#${this._swiperId}`, options);
        var $swiperContainer = document.getElementById(this._swiperId);

        scrollClasses(this._swiper, $swiperContainer);
    }
}

window.initializeSwiperSlider = function (id, options = {}) {
    /** @type {Record<string, any>} */
    let config = {
        direction: "horizontal",
        slidesPerView: options.perView || 1,
        spaceBetween: options.gap || 24,
        watchSlidesProgress: true,
        centerInsufficientSlides: options.centerSlides || false,
        breakpoints: options.breakpoints || [],
        navigation: {
            nextEl: "#" + id + "> .swiper-button-next",
            prevEl: "#" + id + "> .swiper-button-prev",
        },
        pagination: {
            el: "#" + id + "> .swiper-pagination",
            clickable: true,
        },
        initialSlide: options.initialSlide || 0,
        centeredSlides: options.focusCenter || false,
        loop: options.loop || false,
        autoplay: {
            enabled: options.autoplay || false,
            delay: options.autoplayDelay || 10000,
            pauseOnMouseEnter: true,
        },
    };

    const swiperEl = document.getElementById(id);
    const thumbsId = swiperEl?.dataset?.thumbsId;

    if (thumbsId && window.swipers[thumbsId]) {
        config.thumbs = {
            swiper: window.swipers[thumbsId]._swiper,
        };
    }

    window.swipers[id] = new swiperSlider(id, config);
};

window.initializeSwiperScrollHorizontal = function (id, options = {}) {
    window.swipers[id] = new swiperScroll(id, {
        direction: "horizontal",
        slidesPerView: "auto",
        freeMode: true,
        watchSlidesProgress: true,
        centerInsufficientSlides: options.centerSlides || false,
        scrollbar: {
            el: "#" + id + "> .swiper-scrollbar",
            draggable: true,
        },
        navigation: {
            nextEl: "#" + id + "> .swiper-button-next",
            prevEl: "#" + id + "> .swiper-button-prev",
        },
        pagination: {
            el: "#" + id + "> .swiper-pagination",
            clickable: true,
        },
        mousewheel: {
            forceToAxis: true,
        },
        on: {
            /**
             * Slide to the initial slide after a short delay on init.
             *
             * @param {Swiper} swiper - The Swiper instance.
             * @returns {void}
             */
            init: function (swiper) {
                setTimeout(function () {
                    swiper.slideTo((options.initialSlide || 1) - 1, 800, false);
                }, 500);
            },
        },
    });
};

window.initializeSwiperScrollVertical = function (id) {
    window.swipers[id] = new swiperScroll(id, {
        direction: "vertical",
        slidesPerView: "auto",
        freeMode: true,
        watchSlidesProgress: true,
        scrollbar: {
            el: "#" + id + "> .swiper-scrollbar",
            draggable: true,
        },
        mousewheel: {
            forceToAxis: true,
        },
    });
};

/**
 * Initialise a horizontal Swiper scroll by DOM id, reading its options from data-attributes.
 *
 * @param {string} id - The scroll element's id.
 * @returns {void}
 */
window.initScrollHorizontalById = function (id) {
    const swiper = document.getElementById(id);
    if (!swiper) return;

    const options = window.swiperOptions.getScrollOptions(swiper);
    window.initializeSwiperScrollHorizontal(id, options);
};

/**
 * Initialise a Swiper slider by DOM id, plus its thumbnail slider if present.
 *
 * @param {string} id - The slider element's id.
 * @returns {void}
 */
window.initSwiperSliderWithThumbs = function (id) {
    const swiper = document.getElementById(id);
    if (!swiper) return;

    const options = window.swiperOptions.getSliderOptions(swiper);
    const thumbsId = swiper.dataset.thumbsId;

    if (thumbsId) {
        const thumbsElement = document.getElementById(thumbsId);
        if (thumbsElement) {
            const thumbsOptions =
                window.swiperOptions.getSliderOptions(thumbsElement);
            window.initializeSwiperSlider(thumbsId, thumbsOptions);
        }
    }

    window.initializeSwiperSlider(id, options);
};
